import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES, type ExecSubtype, type Role } from '@saledeed/shared';
import { api } from '../api/client';
import type { CreatedUser, NewUserInput, User } from '../api/types';
import { useUser } from '../auth/session';
import { Badge, Button, ErrorBox, Field, formatDateTime, Modal, Notice, Select, Spinner, TableCard, TextInput } from '../components/ui';

const ROLE_LABEL: Record<Role, string> = { admin: 'Admin', manager: 'Manager', executive: 'Executive' };
const SUBTYPE_LABEL: Record<ExecSubtype, string> = { view: 'View-restricted', editor: 'Editor' };

function roleText(u: Pick<User, 'role' | 'execSubtype'>) {
  return u.role === 'executive' ? `Executive · ${SUBTYPE_LABEL[u.execSubtype ?? 'view']}` : ROLE_LABEL[u.role];
}

/** Shown exactly once: the Worker keeps only a hash of the code. */
function SetupCodeModal({ info, onClose }: { info: { name: string; code: string; expiresAt: string } | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const link = info ? `${location.origin}/setup#code=${encodeURIComponent(info.code)}` : '';
  return (
    <Modal open={!!info} title="Setup code" onClose={() => { setCopied(false); onClose(); }}>
      {info && (
        <div className="flex flex-col gap-3 text-sm">
          <p>Give this to <strong>{info.name}</strong>. They use it once to choose their own password. It expires {formatDateTime(info.expiresAt)}.</p>
          <Notice tone="warning">It is shown only now and cannot be retrieved later. If it is lost, reset the account for a new one.</Notice>
          <code className="block break-all rounded bg-slate-100 p-2 text-base">{info.code}</code>
          <p className="text-xs text-slate-600">Or send this link (the code is after the #, so it is never sent to the server in the address):</p>
          <code className="block break-all rounded bg-slate-100 p-2 text-xs">{link}</code>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { void navigator.clipboard.writeText(link).then(() => setCopied(true)); }}>{copied ? 'Copied' : 'Copy link'}</Button>
            <Button tone="primary" onClick={onClose}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function CreateUserForm({ onCreated }: { onCreated: (r: CreatedUser) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<NewUserInput>({ email: '', displayName: '', role: 'executive', execSubtype: 'view' });
  const create = useMutation({
    mutationFn: () => api<CreatedUser>('POST', '/api/admin/users', { body: { ...form, execSubtype: form.role === 'executive' ? form.execSubtype : null } }),
    onSuccess: r => { setForm({ email: '', displayName: '', role: form.role, execSubtype: form.execSubtype }); void qc.invalidateQueries({ queryKey: ['users'] }); onCreated(r); },
  });
  return (
    <form className="grid grid-cols-1 gap-3 rounded border border-slate-200 p-4 md:grid-cols-5" onSubmit={e => { e.preventDefault(); create.mutate(); }}>
      <Field label="Name">{id => <TextInput id={id} required maxLength={120} value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} />}</Field>
      <Field label="Email">{id => <TextInput id={id} type="email" required maxLength={254} autoComplete="off" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />}</Field>
      <Field label="Role">{id => (
        <Select id={id} value={form.role} onChange={e => setForm({ ...form, role: e.target.value as Role })}>
          {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </Select>
      )}</Field>
      {form.role === 'executive' ? (
        <Field label="Executive type" hint={form.execSubtype === 'view' ? 'Fills fields; sees the layout, not the wording' : 'Can also edit the wording'}>{id => (
          <Select id={id} value={form.execSubtype ?? 'view'} onChange={e => setForm({ ...form, execSubtype: e.target.value as ExecSubtype })}>
            <option value="view">View-restricted</option><option value="editor">Editor</option>
          </Select>
        )}</Field>
      ) : <div />}
      <div className="flex items-end"><Button tone="primary" type="submit" disabled={create.isPending}>{create.isPending ? 'Adding…' : 'Add user'}</Button></div>
      <div className="md:col-span-5"><ErrorBox error={create.error} /></div>
    </form>
  );
}

export function UsersPage() {
  const me = useUser();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<{ users: User[] }>('GET', '/api/admin/users') });
  const [code, setCode] = useState<{ name: string; code: string; expiresAt: string } | null>(null);

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api<{ user: User }>('PATCH', `/api/admin/users/${id}`, { body }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
  const reset = useMutation({
    mutationFn: (u: User) => api<{ setupCode: string; setupCodeExpiresAt: string }>('POST', `/api/admin/users/${u.id}/reset`).then(r => ({ u, r })),
    onSuccess: ({ u, r }) => { setCode({ name: u.displayName, code: r.setupCode, expiresAt: r.setupCodeExpiresAt }); void qc.invalidateQueries({ queryKey: ['users'] }); },
  });

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4 p-6">
      <h1 className="text-xl font-semibold">People</h1>
      <p className="text-sm text-slate-600">New accounts get a one-time setup code; there are no default passwords. A role change applies from that person's next action; disabling signs them out at once.</p>
      <CreateUserForm onCreated={r => setCode({ name: r.user.displayName, code: r.setupCode, expiresAt: r.setupCodeExpiresAt })} />
      <ErrorBox error={patch.error ?? reset.error} />
      {users.isPending ? <Spinner /> : users.isError ? <ErrorBox error={users.error} /> : (
        <TableCard>
          <caption className="sr-only">People</caption>
          <thead><tr className="border-b border-slate-300 text-left"><th scope="col" className="py-2">Name</th><th scope="col">Email</th><th scope="col">Code</th><th scope="col">Role</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
          <tbody>
            {users.data.users.map(u => (
              <tr key={u.id} className="border-b border-slate-200">
                <td className="py-2 font-medium">{u.displayName}{u.id === me.user.id && ' (you)'}</td>
                <td>{u.email}</td>
                <td><code className="text-xs">{u.userCode}</code></td>
                <td>
                  <label className="sr-only" htmlFor={`role-${u.id}`}>Role of {u.displayName}</label>
                  <select id={`role-${u.id}`} className="rounded border border-slate-300 px-1 py-0.5" disabled={u.id === me.user.id || patch.isPending}
                    value={u.role === 'executive' ? `executive:${u.execSubtype}` : u.role}
                    onChange={e => {
                      const [role, subtype] = e.target.value.split(':') as [Role, ExecSubtype | undefined];
                      if (confirm(`Change ${u.displayName} to ${roleText({ role, execSubtype: subtype ?? null })}? It applies from their next action.`)) {
                        patch.mutate({ id: u.id, body: { role, execSubtype: subtype ?? null } });
                      }
                    }}>
                    <option value="admin">Admin</option><option value="manager">Manager</option>
                    <option value="executive:view">Executive · View-restricted</option><option value="executive:editor">Executive · Editor</option>
                  </select>
                </td>
                <td><Badge tone={u.status === 'active' ? 'green' : u.status === 'invited' ? 'blue' : 'red'}>{u.status}</Badge></td>
                <td className="flex justify-end gap-1 py-1">
                  {u.id !== me.user.id && u.status !== 'disabled' && (
                    <Button tone="ghost" onClick={() => { if (confirm(`Reset ${u.displayName}? Their password is removed and they get a new setup code.`)) reset.mutate(u); }}>Reset</Button>
                  )}
                  {u.id !== me.user.id && (u.status === 'disabled'
                    ? <Button tone="ghost" onClick={() => patch.mutate({ id: u.id, body: { status: 'active' } })}>Enable</Button>
                    : u.status === 'active' && <Button tone="ghost" onClick={() => { if (confirm(`Disable ${u.displayName}? They are signed out at once.`)) patch.mutate({ id: u.id, body: { status: 'disabled' } }); }}>Disable</Button>)}
                </td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      )}
      <SetupCodeModal info={code} onClose={() => setCode(null)} />
    </div>
  );
}
